import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FiSearch, FiArrowRight } from 'react-icons/fi';
import axios from 'axios';
import { API_URL } from '../config';

// Full-page search results — a roomier view than the sidebar dropdown, so you can actually
// read portal messages, emails, work orders, notes, etc. Reuses GET /api/search.
export default function SearchResults() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const urlQ = params.get('q') || '';
  const [q, setQ] = useState(urlQ);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const run = useCallback(async (term) => {
    const t = (term || '').trim();
    if (t.length < 2) { setGroups([]); setSearched(false); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await axios.get(`${API_URL}/search?q=${encodeURIComponent(t)}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setGroups(res.data.groups || []);
      setSearched(true);
    } catch (e) {
      setGroups([]); setSearched(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-run whenever the URL ?q= changes (from the sidebar, a link, or submitting here).
  useEffect(() => { setQ(urlQ); run(urlQ); }, [urlQ, run]);

  const submit = (e) => {
    e.preventDefault();
    setParams(q.trim() ? { q: q.trim() } : {});
  };

  const total = groups.reduce((n, g) => n + (g.items ? g.items.length : 0), 0);

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 20px' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: '0 0 16px' }}>Search</h1>

      <form onSubmit={submit} style={{ position: 'relative', marginBottom: 20 }}>
        <FiSearch size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
          placeholder="Search jobs, clients, contacts, notes, portal messages, emails, work orders…"
          style={{ width: '100%', padding: '13px 16px 13px 42px', borderRadius: 10, border: '1px solid #cbd5e1', fontSize: 15, outline: 'none', boxSizing: 'border-box' }}
        />
      </form>

      {loading ? (
        <div style={{ color: '#64748b', fontSize: 14, padding: 20 }}>Searching…</div>
      ) : !searched ? (
        <div style={{ color: '#94a3b8', fontSize: 14, padding: 20 }}>Type at least 2 characters to search.</div>
      ) : total === 0 ? (
        <div style={{ color: '#64748b', fontSize: 14, padding: 20, background: 'white', borderRadius: 12, border: '1px solid #e5e7eb' }}>
          No results for &ldquo;{urlQ}&rdquo;.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>{total} result{total === 1 ? '' : 's'} for &ldquo;{urlQ}&rdquo;</div>
          {groups.map((g) => (
            <div key={g.type} style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', marginBottom: 8 }}>
                {g.label} <span style={{ color: '#cbd5e1', fontWeight: 600 }}>· {g.items.length}</span>
              </div>
              <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                {g.items.map((it, i) => (
                  <button key={g.type + i} onClick={() => navigate(it.link)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, width: '100%', textAlign: 'left', padding: '13px 16px', background: 'none', border: 'none', borderTop: i ? '1px solid #f1f5f9' : 'none', cursor: 'pointer' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'none'}>
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span style={{ fontWeight: 600, color: '#0f172a', fontSize: 14.5 }}>{it.title}</span>
                      {it.subtitle ? <div style={{ color: '#475569', fontSize: 13.5, marginTop: 3, lineHeight: 1.4 }}>{it.subtitle}</div> : null}
                    </span>
                    {it.meta ? <span style={{ color: '#94a3b8', fontSize: 13, whiteSpace: 'nowrap', flexShrink: 0 }}>{it.meta}</span> : null}
                    <FiArrowRight size={15} style={{ color: '#cbd5e1', flexShrink: 0 }} />
                  </button>
                ))}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
